/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { Alert, Input } from 'antd'
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import React, { useCallback } from 'react'
import Text from 'src/components/ts-components/Text'
import { useVideographerContext } from './VideographerContext'

const PLACEHOLDER_TEAM_IMAGE =
  '/images/generic_placeholders/placeholder_team.png'

const MAX_VIDEOGRAPHERS = 5

const TeamSelectionStep: React.FC = () => {
  const {
    state,
    homeTeamName,
    visitorTeamName,
    homeRosterId,
    visitorRosterId,
    homeTeamImage,
    visitorTeamImage,
    onSelectTeam,
  } = useVideographerContext()

  const renderTeamOption = (
    team: 'home' | 'visitor',
    name: string,
    count: number,
    disabled: boolean,
    hasRoster: boolean,
    teamImage?: string | number | null
  ) => {
    const isDisabledNoRoster = !hasRoster
    const isDisabledFull = disabled
    const imageSrc = teamImage
      ? getImageThumbnailId(teamImage)
      : PLACEHOLDER_TEAM_IMAGE

    return (
      <button
        key={team}
        disabled={isDisabledNoRoster || isDisabledFull}
        onClick={() => onSelectTeam(team)}
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 16px;
          background: ${colors.SECONDARY_400};
          border: 1px solid ${colors.SECONDARY_100};
          border-radius: 8px;
          cursor: ${isDisabledNoRoster || isDisabledFull
            ? 'not-allowed'
            : 'pointer'};
          opacity: ${isDisabledNoRoster || isDisabledFull ? 0.5 : 1};
          transition: all 0.2s;
          &:hover {
            background: ${isDisabledNoRoster || isDisabledFull
              ? colors.SECONDARY_400
              : colors.SECONDARY_300};
          }
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          <img
            src={imageSrc}
            alt={name}
            css={css`
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
              background: ${colors.SECONDARY_300};
              border: 1px solid ${colors.SECONDARY_100};
            `}
          />
          <div
            css={css`
              text-align: left;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='semiBold'
              color={colors.WHITE}
            >
              {name}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              {team === 'home' ? 'Home' : 'Visitor'}
            </Text>
          </div>
        </div>
        <div
          css={css`
            text-align: right;
          `}
        >
          {isDisabledNoRoster ? (
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              No roster
            </Text>
          ) : isDisabledFull ? (
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WARNING_300}
            >
              Full ({MAX_VIDEOGRAPHERS}/{MAX_VIDEOGRAPHERS})
            </Text>
          ) : (
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              {count}/{MAX_VIDEOGRAPHERS}
            </Text>
          )}
        </div>
      </button>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <Text
        variant='paragraphMedium'
        weight='regular'
        color={colors.NEUTRAL_70}
      >
        Select Which Team The Videographer Will Stream For:
      </Text>

      {renderTeamOption(
        'home',
        homeTeamName || 'Home Team',
        state.homeCount,
        state.homeDisabled,
        !!homeRosterId,
        homeTeamImage
      )}

      {renderTeamOption(
        'visitor',
        visitorTeamName || 'Visitor Team',
        state.visitorCount,
        state.visitorDisabled,
        !!visitorRosterId,
        visitorTeamImage
      )}

      {state.homeDisabled && state.visitorDisabled && (
        <Alert
          message='Both Teams Have Reached The Maximum Number Of Videographers'
          type='warning'
          showIcon
        />
      )}
    </div>
  )
}

const EmailFormStep: React.FC = () => {
  const {
    state,
    dispatch,
    homeTeamName,
    visitorTeamName,
    homeTeamImage,
    visitorTeamImage,
    onBack,
  } = useVideographerContext()

  const teamName =
    state.selectedTeam === 'home'
      ? homeTeamName || 'Home Team'
      : visitorTeamName || 'Visitor Team'

  const teamImage =
    state.selectedTeam === 'home' ? homeTeamImage : visitorTeamImage
  const teamImageSrc = teamImage
    ? getImageThumbnailId(teamImage)
    : PLACEHOLDER_TEAM_IMAGE

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'SET_EMAIL', email: e.target.value })
    },
    [dispatch]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <button
        onClick={onBack}
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          color: ${colors.NEUTRAL_70};
          transition: color 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      >
        <ArrowLeftOutlined />
        <Text variant='paragraphSmall' weight='regular' color='inherit'>
          Back
        </Text>
      </button>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: ${colors.SECONDARY_400};
          border-radius: 8px;
        `}
      >
        <img
          src={teamImageSrc}
          alt={teamName}
          css={css`
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
            background: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
          `}
        />
        <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
          {teamName}
        </Text>
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
          Videographer Email
        </Text>
        <Input
          prefix={
            <MailOutlined
              css={css`
                color: ${colors.NEUTRAL_70};
              `}
            />
          }
          placeholder='Enter Email Address'
          value={state.email}
          onChange={handleEmailChange}
          disabled={state.isLoading}
          status={state.error ? 'error' : undefined}
          css={css`
            background: ${colors.SECONDARY_400} !important;
            border-color: ${state.error
              ? colors.ERROR_200
              : colors.SECONDARY_100} !important;
            color: ${colors.WHITE} !important;
            padding: 12px;
            &::placeholder {
              color: ${colors.NEUTRAL_70};
            }
            .ant-input {
              background: transparent !important;
              color: ${colors.WHITE} !important;
            }
            &:hover,
            &:focus {
              border-color: ${state.error
                ? colors.ERROR_200
                : isCanlan
                ? colors.CYAN_BLUE
                : colors.AMERICAN_YELLOW} !important;
            }
          `}
        />
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_70}
        >
          An Invitation Will Be Sent To This Email. The Videographer Will Be
          Able To Stream Once They Accept The Invitation.
        </Text>
      </div>

      {state.error && <Alert message={state.error} type='error' showIcon />}
    </div>
  )
}

const VideographerModalContent: React.FC = () => {
  const { state } = useVideographerContext()

  return state.step === 'team-selection' ? (
    <TeamSelectionStep />
  ) : (
    <EmailFormStep />
  )
}

export default VideographerModalContent
